import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { EVenue, PlayType } from '@/apis/commonSports/constants';
import {
  FOLLOW_MATCH_IDS_KEY,
  PINNED_SPORT_IDS_KEY,
  PINNED_MATCH_IDS_KEY,
  SYNC_SINGLE_PARLAY_KEY,
  IS_SIMPLE_ODDS_KEY,
  IS_OPEN_GOAL_SOUND_KEY,
  HIDE_BET_DRAWER_APP_DOWNLOAD_KEY,
} from '@/utils/constants/cacheKey';
import { isSSR } from '@/utils/env';
import { MenuInfo } from '@/apis/commonSports/types';
import { FBCompetitionMap, FBSportIdValue, MatchPlayType } from '@/apis/fbSports/common/constants';
import { LocalHandicapItem } from '@/apis/fbSports/common/types';
import { ESportsLeftPanelType } from '@/apis/commonSports/constants';

/**
 * 娱乐大厅State
 */
export interface PinnedMatch {
  matchId: number;
  sportId: number;
  playType: PlayType;
}
export interface followMatch {
  matchId: number;
  sportId: number;
}
export interface SportState {
  venue: EVenue;
  mainList: {
    settings: {
      sportId: number;
      favoriteSportId: number;
      matchDateId: number;
      playType: PlayType;
      playTypeId: number | null;
      filterByLeagueIds: number[];
      /** 联赛筛选是否与「筛选」Tab 勾选同步；热门搜索生效的筛选为 false，仅搜索 Tab 回显 */
      filterLeaguePickerSynced: boolean;
      filterSearchText: string; // 赛事搜索的文字 只用于回显
      orderBy: number; // 排序 0 按开赛时间排序，1 按联赛排序， 传：0或1 默认 1 按联赛
      filterTime: number[]; // 早盘 时间筛选 空数组为全部 否则传13位时间戳
      followMatch: followMatch[];
      collapsedAll: boolean;
      simpleActiveItem: LocalHandicapItem | null;
      isSimpleOdds: boolean; // 是否为简洁版盘口样式
      hasHotList: boolean; // 是否含有热门赛事列表
    };
    datas: {
      pinnedSportIds: number[]; // 置顶的赛种id（目前只有热门可以置顶）
      pinnedMatchs: Array<PinnedMatch>;
      menuInfo: MenuInfo;
    };
  };
  /** PC体育页面，左侧菜单展示内容 */
  sportsLeftPanelType: ESportsLeftPanelType;
  /** 是否开启同步单串 */
  syncSingleParlay?: boolean;
  /** 是否开启进球铃声 */
  isOpenGoalSound?: boolean;
  /** PC投注抽屉,是否隐藏app下载模块(默认不隐藏) */
  hideBetDrawerDownloadApp: boolean;
}

type TStorageSportState = Pick<
  SportState,
  'syncSingleParlay' | 'isOpenGoalSound' | 'hideBetDrawerDownloadApp'
>;

const initialStateSSR: TStorageSportState = {
  syncSingleParlay: true,
  isOpenGoalSound: false,
  hideBetDrawerDownloadApp: false,
};

const getInitialState = (): TStorageSportState => {
  if (isSSR()) {
    return initialStateSSR;
  }
  return {
    syncSingleParlay:
      localStorage.getItem(SYNC_SINGLE_PARLAY_KEY) === 'true' ||
      localStorage.getItem(SYNC_SINGLE_PARLAY_KEY) === null,
    isOpenGoalSound: localStorage.getItem(IS_OPEN_GOAL_SOUND_KEY) === 'true',
    hideBetDrawerDownloadApp: localStorage.getItem(HIDE_BET_DRAWER_APP_DOWNLOAD_KEY) === 'true',
  };
};

export const initialState: SportState = {
  venue: EVenue.FB,
  mainList: {
    settings: {
      sportId: FBSportIdValue.Football,
      // sportId: 1,
      favoriteSportId: 0,
      matchDateId: 0,
      playType: PlayType.Living,
      playTypeId: MatchPlayType.LIVE,
      filterByLeagueIds: [],
      filterLeaguePickerSynced: true,
      filterSearchText: '',
      collapsedAll: false,
      followMatch: isSSR()
        ? []
        : (JSON.parse(localStorage.getItem(FOLLOW_MATCH_IDS_KEY) ?? '[]') as followMatch[]),
      simpleActiveItem: null,
      orderBy: 1, // 排序 0 按开赛时间排序，1 按联赛排序，传：0或1 默认 1 按联赛
      filterTime: [], // 早盘 时间筛选 空数组为全部 否则传13位时间戳
      isSimpleOdds: isSSR() ? false : localStorage.getItem(IS_SIMPLE_ODDS_KEY) === 'true',
      hasHotList: false,
    },
    datas: {
      pinnedSportIds: isSSR()
        ? []
        : (JSON.parse(localStorage.getItem(PINNED_SPORT_IDS_KEY) ?? '[]') as number[]),
      pinnedMatchs: isSSR()
        ? []
        : (JSON.parse(localStorage.getItem(PINNED_MATCH_IDS_KEY) ?? '[]') as PinnedMatch[]),
      menuInfo: {
        hotSportMatchIds: [],
        menus: {
          [PlayType.Today]: [],
          [PlayType.Early]: [],
          [PlayType.Living]: [],
          [PlayType.Champion]: [],
          [PlayType.Follow]: [],
        },
        playTypes: [],
      },
    },
  },
  sportsLeftPanelType: ESportsLeftPanelType.MENU,
  ...getInitialState(),
};

const sportSlice = createSlice({
  name: 'sport',
  initialState,
  reducers: {
    // 更改首页主列表相关设置
    changeMainListSettings: (
      state,
      action: PayloadAction<Partial<SportState['mainList']['settings']>>,
    ) => {
      state.mainList.settings = {
        ...state.mainList.settings,
        ...action.payload,
      };
    },
    // 添加/移除关注赛事
    setFollowMatchIds: (
      state,
      action: PayloadAction<{
        type: 'add' | 'remove' | 'set';
        matchInfos?: followMatch[];
        allMatchInfos?: followMatch[];
      }>,
    ) => {
      const { type, matchInfos, allMatchInfos } = action.payload;
      const isAdd = type === 'add';
      const isSet = type === 'set';

      if (isSet && allMatchInfos !== undefined) {
        state.mainList.settings.followMatch = allMatchInfos;
      } else if (isAdd && matchInfos) {
        const existingIds = new Set(state.mainList.settings.followMatch.map((m) => m.matchId));
        matchInfos?.forEach((m) => {
          if (!existingIds.has(m.matchId)) {
            state.mainList.settings.followMatch.push(m);
            existingIds.add(m.matchId);
          }
        });
      } else if (!isAdd && matchInfos) {
        const removeIds = new Set(matchInfos?.map((m) => m.matchId));
        state.mainList.settings.followMatch = _.filter(
          state.mainList.settings.followMatch,
          (item) => !removeIds.has(item.matchId),
        );
      }

      localStorage.setItem(
        FOLLOW_MATCH_IDS_KEY,
        JSON.stringify(state.mainList.settings.followMatch),
      );
      const { sportId, playType, followMatch } = state.mainList.settings;

      if (!isAdd && !isSet && playType === PlayType.Follow) {
        // 如果当前展示的类型是关注列表，查看移除后赛种分类下还有没有其他赛事，如果没有就将当前的赛种类型切换到关注的赛种类型的第一个去
        const followMatchSportIds = _.map(followMatch, (item) => item.sportId);
        if (!followMatchSportIds.includes(sportId)) {
          state.mainList.settings.sportId = followMatchSportIds[0] ?? 0;
        }
      }
    },
    // 添加/移除置顶赛种
    setPinnedSportIds: (
      state,
      action: PayloadAction<{ type: 'add' | 'remove'; sportId: number }>,
    ) => {
      const { type, sportId } = action.payload;
      if (type === 'add') {
        state.mainList.datas.pinnedSportIds.push(sportId);
      } else {
        state.mainList.datas.pinnedSportIds = _.difference(state.mainList.datas.pinnedSportIds, [
          sportId,
        ]);
      }
      localStorage.setItem(
        PINNED_SPORT_IDS_KEY,
        JSON.stringify(state.mainList.datas.pinnedSportIds),
      );
    },
    // 添加/移除置顶比赛
    setPinnedMatchIds: (
      state,
      action: PayloadAction<{
        type: 'add' | 'remove' | 'set';
        matchId?: number;
        allMatchInfos?: PinnedMatch[];
      }>,
    ) => {
      const { type, matchId, allMatchInfos } = action.payload;
      const { sportId, playType } = state.mainList.settings;
      switch (type) {
        case 'add':
          state.mainList.datas.pinnedMatchs.push({ matchId: matchId ?? 0, sportId, playType });
          break;
        case 'remove':
          state.mainList.datas.pinnedMatchs = state.mainList.datas.pinnedMatchs.filter(
            (item) =>
              item.matchId !== matchId || item.sportId !== sportId || item.playType !== playType,
          );
          break;
        case 'set':
          state.mainList.datas.pinnedMatchs = allMatchInfos ?? [];
          break;
      }
      localStorage.setItem(PINNED_MATCH_IDS_KEY, JSON.stringify(state.mainList.datas.pinnedMatchs));
    },
    // 设置菜单数据
    setMenus: (state, action: PayloadAction<MenuInfo>) => {
      if (!state.mainList.settings.simpleActiveItem) {
        // 简洁版初始化赔率玩法项
        state.mainList.settings.simpleActiveItem =
          Object.values(FBCompetitionMap).find(
            (item) => item.id === state.mainList.settings.sportId,
          )?.simpleList[0] ?? null;
      }
      state.mainList.datas.menuInfo = { ...state.mainList.datas.menuInfo, ...action.payload };
    },

    // region 设置左侧菜单展示内容
    setSportsLeftPanelType: (state, action: PayloadAction<ESportsLeftPanelType>) => {
      state.sportsLeftPanelType = action.payload;
    },
    // #endregion

    // region 切换同步单串
    toggleSyncSingleParlayAction: (state) => {
      const _syncSingleParlay = !state.syncSingleParlay;
      state.syncSingleParlay = _syncSingleParlay;
      localStorage.setItem(SYNC_SINGLE_PARLAY_KEY, JSON.stringify(_syncSingleParlay));
    },
    // 直接设置同步单串状态，供游客配置/会员配置同步使用
    setSyncSingleParlayAction: (state, action: PayloadAction<boolean>) => {
      state.syncSingleParlay = action.payload;
      localStorage.setItem(SYNC_SINGLE_PARLAY_KEY, JSON.stringify(action.payload));
    },
    // #endregion

    // region PC投注展示APP下载
    toggleHideBetDrawerDownloadAppAction: (state) => {
      const _hideBetDrawerDownloadApp = !state.hideBetDrawerDownloadApp;
      state.hideBetDrawerDownloadApp = _hideBetDrawerDownloadApp;
      localStorage.setItem(
        HIDE_BET_DRAWER_APP_DOWNLOAD_KEY,
        JSON.stringify(_hideBetDrawerDownloadApp),
      );
    },
    // #endregion

    toggleIsOpenGoalSoundAction: (state) => {
      const _isOpenGoalSound = !state.isOpenGoalSound;
      state.isOpenGoalSound = _isOpenGoalSound;
      localStorage.setItem(IS_OPEN_GOAL_SOUND_KEY, JSON.stringify(_isOpenGoalSound));
    },
    // 直接设置进球铃声状态，供游客配置/会员配置同步使用
    setIsOpenGoalSoundAction: (state, action: PayloadAction<boolean>) => {
      state.isOpenGoalSound = action.payload;
      localStorage.setItem(IS_OPEN_GOAL_SOUND_KEY, JSON.stringify(action.payload));
    },
    // 直接设置盘口样式，供游客配置/会员配置同步使用
    setIsSimpleOddsAction: (state, action: PayloadAction<boolean>) => {
      state.mainList.settings.isSimpleOdds = action.payload;
      localStorage.setItem(IS_SIMPLE_ODDS_KEY, action.payload ? 'true' : 'false');
    },
  },
});

export const {
  changeMainListSettings,
  setFollowMatchIds,
  setPinnedSportIds,
  setPinnedMatchIds,
  setMenus,
  toggleSyncSingleParlayAction,
  setSyncSingleParlayAction,
  toggleHideBetDrawerDownloadAppAction,
  toggleIsOpenGoalSoundAction,
  setIsOpenGoalSoundAction,
  setIsSimpleOddsAction,
  setSportsLeftPanelType,
} = sportSlice.actions;

export default sportSlice.reducer;
